import { useSelector } from 'react-redux';
import { OrderBulkDto } from '@tiendanube/common';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { NUVEMENVIO_LABEL_PRINTING } from 'App/featuresFlags';
import { useAppDispatch } from 'App/store';
import { useToast } from 'commons/hooks';
import { useGetTags } from 'domains/Auth/hooks';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import useNuvemEnvioTranslations from 'domains/NuvemEnvioCommons/hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import {
  trackingNuvemEnvioCreditRechargeLabelsModal,
  trackingNuvemEnvioPrintLabels,
} from 'domains/NuvemEnvioCommons/tracking';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import {
  openNuvemEnvioPrintLabelModal as openNuvemEnvioPrintLabelModalAction,
  closeNuvemEnvioPrintLabelModal as closeNuvemEnvioPrintLabelModalAction,
} from '../../ordersSlice';
import { getShowPrintLabelModal } from '../../ordersSlice/ordersSelectors';
import { extractFulfillmentOrdersIds, isNuvemEnvioLink } from '../../utils';
import useGoToPrintNuvemEnvioLabelsPage from '../useGoToPrintNuvemEnvioLabels/useGoToPrintNuvemEnvioLabels';
import useSimulateNuvemEnvioLabels from '../useSimulateNuvemEnvioLabels/useSimulateNuvemEnvioLabels';

interface UseNuvemEnvioPrintLabelResult {
  hasNuvemEnvioPrintingEnabled: (link?: string) => boolean;
  printNuvemEnvioLabelsFromOrdersList: (
    selectedOrderIds: string[],
    bulkOrders: OrderBulkDto[],
  ) => Promise<void>;
  printNuvemEnvioLabels: (
    source: NE_PRINT_LABEL_SOURCE,
    fulfillmentOrdersIds: string[],
  ) => Promise<void>;
  showNuvemEnvioPrintLabelModal: boolean;
  closeNuvemEnvioPrintLabelModal: () => void;
}

function useNuvemEnvioPrintLabel(): UseNuvemEnvioPrintLabelResult {
  const tags = useGetTags();
  const { id: storeId } = useStoreInfo();
  const { isPostPaid } = useGetNuvemEnvioConfigs();
  const { goToPrintNuvemEnvioLabelsPage } = useGoToPrintNuvemEnvioLabelsPage();
  const { simulateNuvemEnvioLabels } = useSimulateNuvemEnvioLabels();
  const t = useNuvemEnvioTranslations();
  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();
  const dispatch = useAppDispatch();

  const showNuvemEnvioPrintLabelModal = useSelector(getShowPrintLabelModal);

  const enabledByTag = tags.includes(NUVEMENVIO_LABEL_PRINTING);

  const hasNuvemEnvioPrintingEnabled = (link?: string) => {
    if (link) {
      return isNuvemEnvioLink(link) && enabledByTag;
    }
    return enabledByTag;
  };

  const closeNuvemEnvioPrintLabelModal = () => {
    dispatch(closeNuvemEnvioPrintLabelModalAction());
  };

  const printNuvemEnvioLabelsFromOrdersList = async (
    selectedOrderIds: string[],
    bulkOrders: OrderBulkDto[],
  ) => {
    const fulfillmentOrdersIds = extractFulfillmentOrdersIds(
      selectedOrderIds,
      bulkOrders,
    );

    printNuvemEnvioLabels(
      NE_PRINT_LABEL_SOURCE.ORDERS_LIST,
      fulfillmentOrdersIds,
    );
  };

  const printNuvemEnvioLabels = async (
    source: NE_PRINT_LABEL_SOURCE,
    fulfillmentOrdersIds: string[],
  ) => {
    if (isPostPaid) {
      trackingNuvemEnvioPrintLabels(storeId);
      goToPrintNuvemEnvioLabelsPage({
        source,
        fulfillmentOrdersIds,
      });

      return;
    }

    try {
      addToastProgress({
        label: t('creditRechargeNuvemEnvioLabels.toast.wait'),
      });

      const response = await simulateNuvemEnvioLabels(fulfillmentOrdersIds);

      if (response.balanceInfo?.shouldRecharge) {
        trackingNuvemEnvioCreditRechargeLabelsModal(
          storeId,
          fulfillmentOrdersIds.length,
          response.balanceInfo?.suggestedRechargeAmount ?? 0,
          response.balanceInfo?.currentBalanceAmount ?? 0,
        );

        dispatch(openNuvemEnvioPrintLabelModalAction());
      } else {
        trackingNuvemEnvioPrintLabels(storeId);
        goToPrintNuvemEnvioLabelsPage({
          source,
          fulfillmentOrdersIds,
        });
      }
    } catch (error) {
      addToast({
        label: t('creditRechargeNuvemEnvioLabels.error.title'),
        appearance: 'danger',
      });
    } finally {
      closeToastProgress();
    }
  };

  return {
    hasNuvemEnvioPrintingEnabled,
    printNuvemEnvioLabelsFromOrdersList,
    printNuvemEnvioLabels,
    showNuvemEnvioPrintLabelModal,
    closeNuvemEnvioPrintLabelModal,
  };
}

export default useNuvemEnvioPrintLabel;
